import { isOneOf, narrowToOneOf } from "@shared/validation/is-one-of";
import { literalValues } from "@shared/validation/literal-values";

export const ATTRIBUTION_CHANNEL = {
  PAID_GOOGLE: "paid_google",
  PAID_FACEBOOK: "paid_facebook",
  PAID_BING: "paid_bing",
  PAID_TIKTOK: "paid_tiktok",
  PAID_LINKEDIN: "paid_linkedin",
  PAID_OTHER: "paid_other",
  ORGANIC_SEARCH: "organic_search",
  ORGANIC_SOCIAL: "organic_social",
  EMAIL: "email",
  REFERRAL: "referral",
  DIRECT: "direct",
  CHATBOT: "chatbot",
  PARTNER_API: "partner_api",
  API: "api",
  INTERNAL: "internal",
  UNKNOWN: "unknown",
} as const;

export type AttributionChannel =
  (typeof ATTRIBUTION_CHANNEL)[keyof typeof ATTRIBUTION_CHANNEL];

export const ATTRIBUTION_CHANNELS = literalValues(ATTRIBUTION_CHANNEL);

const PAID_ATTRIBUTION_CHANNELS: ReadonlySet<string> = new Set<AttributionChannel>([
  ATTRIBUTION_CHANNEL.PAID_GOOGLE,
  ATTRIBUTION_CHANNEL.PAID_FACEBOOK,
  ATTRIBUTION_CHANNEL.PAID_BING,
  ATTRIBUTION_CHANNEL.PAID_TIKTOK,
  ATTRIBUTION_CHANNEL.PAID_LINKEDIN,
  ATTRIBUTION_CHANNEL.PAID_OTHER,
]);

export function isPaidAttributionChannel(channel: string | undefined): boolean {
  return channel !== undefined && PAID_ATTRIBUTION_CHANNELS.has(channel);
}

export const LEGACY_ATTRIBUTION_CHANNEL_MAP: Record<
  string,
  AttributionChannel
> = {
  partner_network: ATTRIBUTION_CHANNEL.PARTNER_API,
  landing_page: ATTRIBUTION_CHANNEL.DIRECT,
  external_api: ATTRIBUTION_CHANNEL.API,
};

export function normalizeAttributionChannel(
  value: string | undefined,
): AttributionChannel | undefined {
  if (!value) return undefined;
  if (value in LEGACY_ATTRIBUTION_CHANNEL_MAP) {
    return LEGACY_ATTRIBUTION_CHANNEL_MAP[value];
  }
  return narrowToOneOf(ATTRIBUTION_CHANNELS, value);
}

const LEGACY_ATTRIBUTION_ALIAS_TARGETS = new Set<AttributionChannel>(
  Object.values(LEGACY_ATTRIBUTION_CHANNEL_MAP),
);

export function attributionChannelHasExactIndexMatch(
  channel: AttributionChannel,
): boolean {
  return (
    channel !== "unknown" && !LEGACY_ATTRIBUTION_ALIAS_TARGETS.has(channel)
  );
}

export const ATTRIBUTION_CHANNEL_LABEL: Record<AttributionChannel, string> = {
  paid_google: "Google Ads",
  paid_facebook: "Facebook Ads",
  paid_bing: "Bing Ads",
  paid_tiktok: "TikTok Ads",
  paid_linkedin: "LinkedIn Ads",
  paid_other: "Paid (other)",
  organic_search: "Organic Search",
  organic_social: "Organic Social",
  email: "Email",
  referral: "Referral",
  direct: "Direct",
  chatbot: "Chatbot",
  partner_api: "Lead-gen site",
  api: "API",
  internal: "Internal",
  unknown: "Unknown",
};

export function isValidAttributionChannel(
  value: unknown,
): value is AttributionChannel {
  return (
    typeof value === "string" && isOneOf(ATTRIBUTION_CHANNELS, value)
  );
}

export const ORGANIC_SEARCH_HOST_STEMS: Array<string> = [
  "google.",
  "bing.",
  "duckduckgo.",
  "yahoo.",
  "brave.",
  "ecosia.",
  "kagi.",
  "startpage.",
  "chatgpt.",
  "openai.",
  "claude.",
  "gemini.google.",
  "perplexity.",
  "copilot.microsoft.",
];

export const ORGANIC_SOCIAL_HOST_STEMS: Array<string> = [
  "facebook.",
  "instagram.",
  "linkedin.",
  "lnkd.in",
  "twitter.",
  "x.com",
  "t.co",
  "tiktok.",
  "pinterest.",
  "reddit.",
  "youtube.",
  "youtu.be",
  "snapchat.",
  "threads.net",
];

export const PAID_MEDIUMS = new Set<string>([
  "cpc",
  "ppc",
  "paid",
  "paid_social",
  "paidsocial",
  "paid-search",
  "paid-social",
  "cpm",
  "display",
  "banner",
  "retargeting",
  "remarketing",
]);

export const FACEBOOK_UTM_SOURCES = new Set<string>([
  "facebook",
  "meta",
  "fb",
  "instagram",
  "ig",
]);

export const GOOGLE_UTM_SOURCES = new Set<string>([
  "google",
  "adwords",
  "google-ads",
  "googleads",
  "google_ads",
]);

export const BING_UTM_SOURCES = new Set<string>(["bing", "microsoft"]);

export const TIKTOK_UTM_SOURCES = new Set<string>([
  "tiktok",
  "tiktok_ads",
  "tt",
]);

export const LINKEDIN_UTM_SOURCES = new Set<string>([
  "linkedin",
  "linkedin_ads",
  "li",
]);
